import React, { useEffect, useState } from "react";
import {
  getTenantById,
  getTenantCompetitiveLandscape,
} from "../../../../../shared/services/apis";
import IframeViewer from "../../../../../shared/ui/urlOpener/IFrameViewer";

import jsPDF from "jspdf";

import { RiDownloadLine } from "react-icons/ri";
import {
  Document,
  Paragraph,
  TextRun,
  Table,
  TableRow,
  TableCell,
  Packer,
  HeadingLevel,
  WidthType,
  ImageRun,
  AlignmentType,
  Footer,
} from "docx";
import { saveAs } from "file-saver";
import autoTable from "jspdf-autotable";

export interface OverviewProps {
  tenantId: string;
  tenantServiceId: string;
  tenantServiceOptionId: string;
}

export interface CitationData {
  citations: string[];
  search_results: SearchResult[];
  content: CompanyContent;
}

export interface SearchResult {
  title: string;
  url: string;
  date: string | null;
  last_updated: string | null;
}

export interface CompanyContent {
  company: string;
  overview: string;
  competitive_differentiators: CompetitiveDifferentiators;
  industry_environment: IndustryEnvironment;
  sources: string[];
}

export interface CompetitiveDifferentiators {
  regulatory_and_compliance: string;
  geographic_coverage_and_scale: string;
  technology_advantage: string;
}

export interface IndustryEnvironment {
  characteristics: string;
  trends: string[];
  concentration: IndustryLevelReason;
  barriers_to_entry: IndustryLevelReason;
  regulation_and_policy_impact: IndustryLevelReason;
  life_cycle: string;
  revenue_volatility: IndustryLevelReason;
  innovation_level: IndustryLevelReason;
}

export interface IndustryLevelReason {
  level: string;
  reason: string;
}

export interface Competitor {
  name: string;
  website: string;
  revenue: string;
  employees: number;
  holding_type: string;
  key_offerings: string;
  key_people: KeyPerson[];
  stock_price: string;
  recent_news: string;
  swot: SWOT;
}

export interface KeyPerson {
  name: string;
  role: string;
  linkedin: string;
}

export interface SWOT {
  strengths: string[];
  weaknesses: string[];
  opportunities: string[];
  threats: string[];
}
interface Tenant {
  id: string;
  name: string;
  logoUrl?: string;
}

const Overview: React.FC<OverviewProps> = ({
  tenantId,
  tenantServiceId,
  tenantServiceOptionId,
}) => {
  const [data, setData] = useState<CitationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadingWord, setDownloadingWord] = useState(false);
  const [tenant, setTenant] = useState<Tenant | null>();

  useEffect(() => {
    if (!tenantId) return;

    const fetchTenant = async () => {
      try {
        const data = await getTenantById(tenantId);
        setTenant(data);
      } catch (error) {
        console.error("Error fetching tenant:", error);
      }
    };

    fetchTenant();
  }, [tenantId]);

  useEffect(() => {
    if (!tenantId || !tenantServiceId || !tenantServiceOptionId) return;

    const fetchData = async () => {
      try {
        const response = await getTenantCompetitiveLandscape(
          tenantId,
          tenantServiceId,
          tenantServiceOptionId
        );
        setData(response);
      } catch (err) {
        console.error("Failed to fetch competitor overview:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [tenantId, tenantServiceId, tenantServiceOptionId]);

  const handleSourceClick = (e: React.MouseEvent, url: string) => {
    e.preventDefault();
    setIframeUrl(url);
  };

  const downloadPDF = async () => {
    setDownloading(true);
    try {
      if (!data) return;

      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const leftMargin = 15;
      const rightMargin = 15;
      const contentWidth = pageWidth - leftMargin - rightMargin;

      // === Watermark/logo handling ===
      let watermarkImgData: string | null = null;

      if (tenant?.logoUrl) {
        try {
          const response = await fetch(tenant.logoUrl);
          const blob = await response.blob();
          watermarkImgData = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
        } catch (error) {
          console.warn("Failed to load watermark image:", error);
        }
      }

      const addWatermark = (pageNumber: number) => {
        if (!watermarkImgData) return;
        try {
          doc.setPage(pageNumber);
          doc.saveGraphicsState();
          doc.setGState(doc.GState({ opacity: 0.1 }));

          const watermarkWidth = 60;
          const watermarkHeight = 60;
          const xPos = (pageWidth - watermarkWidth) / 2;
          const yPos = (pageHeight - watermarkHeight) / 2;

          const imgType = watermarkImgData.includes("png") ? "PNG" : "JPEG";
          doc.addImage(
            watermarkImgData,
            imgType,
            xPos,
            yPos,
            watermarkWidth,
            watermarkHeight
          );

          doc.restoreGraphicsState();
        } catch (error) {
          console.warn("Failed to add watermark to page:", error);
        }
      };

      // === Header ===
      doc.setFontSize(17);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(0, 0, 0);
      doc.text("Competitive Landscape - An Overview", pageWidth / 2, 22, {
        align: "center",
      });

      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(100, 100, 100);
      const currentDateTime = new Date().toLocaleString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
      doc.text(`Generated on ${currentDateTime}`, pageWidth / 2, 30, {
        align: "center",
      });

      let y = 45;
      doc.setTextColor(0, 0, 0);

      // === Overview ===
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.text("Company Overview", leftMargin, y);
      y += 7;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);
      const overviewText = doc.splitTextToSize(
        data.content.overview,
        contentWidth
      );
      doc.text(overviewText, leftMargin, y);
      y += overviewText.length * 5 + 8;

      // === Differentiators Section ===
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.text("Competitive Differentiators", leftMargin, y);
      y += 7;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      const differentiators = data.content.competitive_differentiators;

      const diffText1 = `Regulatory & Compliance: ${differentiators.regulatory_and_compliance}`;
      const diffLines1 = doc.splitTextToSize(diffText1, contentWidth - 4);
      doc.text(diffLines1, leftMargin + 4, y);
      y += diffLines1.length * 5 + 3;

      const diffText2 = `Geographic Coverage & Scale: ${differentiators.geographic_coverage_and_scale}`;
      const diffLines2 = doc.splitTextToSize(diffText2, contentWidth - 4);
      doc.text(diffLines2, leftMargin + 4, y);
      y += diffLines2.length * 5 + 3;

      const diffText3 = `Technology Advantage: ${differentiators.technology_advantage}`;
      const diffLines3 = doc.splitTextToSize(diffText3, contentWidth - 4);
      doc.text(diffLines3, leftMargin + 4, y);
      y += diffLines3.length * 5 + 8;

      // === Industry Environment Table ===
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.text("Industry Environment", leftMargin, y);
      y += 6;

      const envHeaders = [["Characteristic", "Trend/Level", "Reason"]];
      const envRows = Object.entries(data.content.industry_environment).map(
        ([key, value]) => {
          let char = key
            .replace(/_/g, " ")
            .replace(/\b\w/g, (l) => l.toUpperCase());
          let trend = "";
          let reason = "";
          if (typeof value === "string") {
            trend = value;
          } else if (Array.isArray(value)) {
            trend = value.join(", ");
          } else if (typeof value === "object" && value !== null) {
            trend = (value as any).level || "";
            reason = (value as any).reason || "";
          }
          return [char, trend, reason];
        }
      );

      autoTable(doc, {
        startY: y,
        head: envHeaders,
        body: envRows,
        styles: {
          fontSize: 9,
          cellPadding: 3,
          overflow: "linebreak",
          valign: "top",
        },
        headStyles: {
          fillColor: [214, 234, 248],
          fontStyle: "bold",
          textColor: [0, 0, 0],
          halign: "left",
        },
        bodyStyles: {
          textColor: [0, 0, 0],
        },
        columnStyles: {
          0: { cellWidth: 40 },
          1: { cellWidth: 35 },
          2: { cellWidth: contentWidth - 75 },
        },
        margin: { left: leftMargin, right: rightMargin },
        didDrawPage: () => {
          // Add watermark to pages created by autoTable
          const pageNum = doc.getNumberOfPages();
          addWatermark(pageNum);
        },
      });

      y = (doc as any).lastAutoTable.finalY + 12;

      // === Sources Section ===
      if (data.content.sources && data.content.sources.length > 0) {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(12);
        doc.setTextColor(0, 0, 0);
        doc.text("Sources", leftMargin, y);
        y += 6;

        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        data.content.sources.forEach((src) => {
          const srcLines = doc.splitTextToSize(src, contentWidth - 4);
          doc.text(srcLines, leftMargin + 4, y);
          y += srcLines.length * 4 + 2;
        });
      }

      // === Watermark and Footer for each page ===
      const totalPages = doc.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        addWatermark(i);
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(`Page ${i} of ${totalPages}`, pageWidth / 2, pageHeight - 10, {
          align: "center",
        });
      }

      doc.save("Overview.pdf");
    } catch (error) {
      alert("Failed to generate PDF: " + error);
    } finally {
      setDownloading(false);
    }
  };
 const downloadWord = async () => {
    if (!data) return;
    setDownloadingWord(true);

    try {
      const base64ToUint8Array = (base64: string): Uint8Array => {
        const binaryString = window.atob(base64);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes;
      };
      // Load logo image if logoUrl exists
      let logoImageData: {
        data: Uint8Array;
        mimeType: "jpg" | "png" | "gif" | "bmp";
      } | null = null;
      if (tenant?.logoUrl) {
        try {
          console.log("Loading logo from:", tenant.logoUrl);
          const response = await fetch(tenant.logoUrl);
          const blob = await response.blob();

          // Map MIME type to docx accepted format
          const getMimeType = (
            blobType: string
          ): "jpg" | "png" | "gif" | "bmp" => {
            if (blobType.includes("png")) return "png";
            if (blobType.includes("jpeg") || blobType.includes("jpg"))
              return "jpg";
            if (blobType.includes("gif")) return "gif";
            if (blobType.includes("bmp")) return "bmp";
            return "png";
          };

          const mimeType = getMimeType(blob.type);
          console.log("Detected MIME type:", mimeType);

          const base64 = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              const result = reader.result as string;
              const base64Data = result.split(",")[1];
              resolve(base64Data);
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });

          const uint8Array = base64ToUint8Array(base64);
          console.log("Logo loaded, size:", uint8Array.length, "bytes");

          logoImageData = { data: uint8Array, mimeType };
        } catch (error) {
          console.error("Failed to load logo image:", error);
        }
      }
      const footerChildren: Paragraph[] = [];

      if (logoImageData) {
        footerChildren.push(
          new Paragraph({
            children: [
              new ImageRun({
                type: logoImageData.mimeType,
                data: logoImageData.data,
                transformation: {
                  width: 60,
                  height: 60,
                },
              }),
            ],
            alignment: AlignmentType.RIGHT,
            spacing: { before: 100 },
          })
        );
      }
      const doc = new Document({
        sections: [
          {
            footers: {
              default: new Footer({
                children: [
                  new Paragraph({
                    children: [
                      new TextRun({
                        text: `Confidential - Generated by ${tenant?.name}`,
                        size: 10,
                        color: "888888",
                      }),
                    ],
                  }),
                  ...footerChildren,
                ],
              }),
            },
            children: [
              new Paragraph({
                text: "Competitive Landscape - An Overview",
                heading: HeadingLevel.TITLE,
                alignment: "center",
                spacing: { after: 200, before: 100 },
              }),
              new Paragraph({
                text: `Generated on ${new Date().toLocaleDateString()}`,
                alignment: "center",
                spacing: { after: 200 },
              }),
              new Paragraph({
                text: "Company Overview",
                heading: HeadingLevel.HEADING_2,
              }),
              new Paragraph({
                text: data.content.overview,
                spacing: { after: 200 },
              }),
              new Paragraph({
                text: "Competitive Differentiators",
                heading: HeadingLevel.HEADING_2,
              }),
              new Paragraph({
                text: [
                  `Regulatory & Compliance: ${data.content.competitive_differentiators.regulatory_and_compliance}`,
                  `Geographic Coverage & Scale: ${data.content.competitive_differentiators.geographic_coverage_and_scale}`,
                  `Technology Advantage: ${data.content.competitive_differentiators.technology_advantage}`,
                ].join("\n"),
                spacing: { after: 200 },
              }),
              new Paragraph({
                text: "Industry Environment",
                heading: HeadingLevel.HEADING_2,
              }),
              // Table for Industry Environment
              new Table({
                rows: [
                  new TableRow({
                    children: [
                      new TableCell({
                        children: [new Paragraph("Characteristic")],
                      }),
                      new TableCell({
                        children: [new Paragraph("Trend/Level")],
                      }),
                      new TableCell({ children: [new Paragraph("Reason")] }),
                    ],
                  }),
                  ...Object.entries(data.content.industry_environment).map(
                    ([key, value]) => {
                      let char = key
                        .replace(/_/g, " ")
                        .replace(/\b\w/g, (l) => l.toUpperCase());
                      let trend = "";
                      let reason = "";
                      if (typeof value === "string") trend = value;
                      else if (Array.isArray(value)) trend = value.join(", ");
                      else if (typeof value === "object" && value !== null) {
                        trend = value.level || "";
                        reason = value.reason || "";
                      }
                      return new TableRow({
                        children: [
                          new TableCell({ children: [new Paragraph(char)] }),
                          new TableCell({ children: [new Paragraph(trend)] }),
                          new TableCell({ children: [new Paragraph(reason)] }),
                        ],
                      });
                    }
                  ),
                ],
                width: { size: 100, type: WidthType.PERCENTAGE },
                columnWidths: [2000, 5000, 3000],
              }),
              // Sources
              ...(data.content.sources && data.content.sources.length > 0
                ? [
                    new Paragraph({
                      text: "Sources",
                      heading: HeadingLevel.HEADING_2,
                    }),
                    ...data.content.sources.map(
                      (src) =>
                        new Paragraph({ text: src, spacing: { after: 50 } })
                    ),
                  ]
                : []),
            ],
          },
        ],
      });

      const blob = await Packer.toBlob(doc);
      saveAs(blob, "Overview.docx");
    } catch {
      alert("faild to download ");
    } finally {
      setDownloadingWord(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-[#FDFDFD] rounded-2xl p-6 shadow-md max-w-4xl mt-0 2xl:mt-20 m-auto overflow-y-auto max-h-[65vh] border border-gray-200 mb-30 animate-pulse">
        {/* Heading */}
        <div className="h-6 w-72 bg-gray-200 rounded mb-4"></div>

        {/* Overview paragraph */}
        <div className="space-y-2 mb-6">
          <div className="h-4 bg-gray-200 rounded w-full"></div>
          <div className="h-4 bg-gray-200 rounded w-5/6"></div>
          <div className="flex gap-2 mt-2">
            <div className="h-6 w-20 bg-gray-200 rounded-full"></div>
            <div className="h-6 w-20 bg-gray-200 rounded-full"></div>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-6 mt-6">
          {/* Left */}
          <div className="flex-1 space-y-6">
            <div className="h-5 w-64 bg-gray-200 rounded mb-4"></div>
            {[...Array(3)].map((_, i) => (
              <div key={i} className="space-y-2">
                <div className="h-4 w-40 bg-gray-200 rounded"></div>
                <div className="h-3 w-2/3 bg-gray-200 rounded"></div>
              </div>
            ))}
          </div>

          {/* Divider */}
          <div className="hidden lg:block border-l border-dashed border-gray-300 mx-6"></div>

          {/* Right */}
          <div className="flex-1">
            <div className="h-5 w-64 bg-gray-200 rounded mb-4"></div>
            <div className="grid grid-cols-2 gap-y-3 text-sm">
              <div className="h-4 w-24 bg-gray-200 rounded"></div>
              <div className="h-4 w-24 bg-gray-200 rounded"></div>

              {[...Array(5)].map((_, i) => (
                <React.Fragment key={i}>
                  <div className="h-3 w-28 bg-gray-200 rounded"></div>
                  <div className="flex gap-2">
                    <div className="h-5 w-14 bg-gray-200 rounded-full"></div>
                    <div className="h-5 w-10 bg-gray-200 rounded-full"></div>
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>

        {/* Sources */}
        <div className="mt-6 flex gap-2">
          <div className="h-4 w-16 bg-gray-200 rounded"></div>
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-6 w-20 bg-gray-200 rounded-full"></div>
          ))}
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center gap-2 text-sm text-red-500 bg-red-50 px-3 py-2 rounded-md mt-4 w-fit">
        <svg
          className="w-4 h-4 text-red-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8v4m0 4h.01m-6.938 4h13.856c1.054 0 1.623-1.14 1.054-2.012L13.054 4.988c-.526-.827-1.582-.827-2.108 0L3.028 17.988c-.57.872.0001 2.012 1.054 2.012z"
          />
        </svg>
        <span>Failed to load data. Please try again.</span>
      </div>
    );
  }

  const industryEnvData = data.content.industry_environment;

  const industryEnvironment = Object.entries(industryEnvData).map(
    ([key, value]) => {
      // Determine display name
      const characteristic = key
        .replace(/_/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase()); // turn "barriers_to_entry" -> "Barriers To Entry"

      // Default values
      let trend = "";
      let reason = null;
      let color = "";

      if (typeof value === "string") {
        trend = value;
      } else if (Array.isArray(value)) {
        trend = value.join(", ");
      } else if (typeof value === "object" && value !== null) {
        trend = value.level || "";
        reason = value.reason || null;

        if (trend === "High") color = "bg-red-500";
        if (trend === "Maturity") color = "bg-red-500";
        if (trend === "Medium") color = "bg-yellow-500";
        if (trend === "Low") color = "bg-green-500";
      }

      return { characteristic, trend, reason, color };
    }
  );

  // const { overview, competitive_differentiators, industry_environment, sources } = data.content;

  return (
    <div className="bg-[#FDFDFD] rounded-2xl p-6 shadow-md max-w-4xl mt-0 2xl:mt-20 m-auto  border border-gray-200 ">
      {/* Heading */}
      <div className="flex gap-2 justify-end">
        <button
          onClick={downloadPDF}
          disabled={downloading}
          className="flex items-center gap-2 hover:bg-gray-200 font-light px-4 py-2 rounded-full border border-gray-300 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
        >
          {downloading ? (
            <>
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Generating...
            </>
          ) : (
            <>
              <RiDownloadLine className="w-4 h-4" /> PDF
            </>
          )}
        </button>

        <button
          onClick={downloadWord}
          disabled={downloadingWord}
          className="flex items-center gap-2 hover:bg-gray-200 font-light px-4 py-2 rounded-full border border-gray-300 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
        >
          {downloadingWord ? (
            <>
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Generating...
            </>
          ) : (
            <>
              <RiDownloadLine className="w-4 h-4" />
              Word
            </>
          )}
        </button>
      </div>
      <h2 className="text-2xl text-gray-800 mb-4">
        Competitive Landscape - An Overview
      </h2>

      {/* Description Paragraphs */}
      {[
        {
          text: data.content.overview,
          sources: data.content.sources?.slice(0, 3) || [],
        },
      ].map((para, index) => (
        <p key={index} className="text-gray-700 font-light mb-4">
          {para.text}
          <div className="my-2">
            {para.sources.map((source, idx) => (
              <button
                key={idx}
                onClick={(e) => handleSourceClick(e, source)}
                className="bg-white border border-gray-200 text-gray-600 text-xs px-2 py-1 mx-1 rounded-full cursor-pointer hover:bg-gray-50"
              >
                {new URL(source).hostname}
              </button>
            ))}
          </div>
        </p>
      ))}

      {/* Content Section */}
      <div className="flex flex-col lg:flex-row gap-6 mt-6">
        {/* Left - Differentiators */}
        <div className="flex-1">
          <h3 className="text-lg font-medium text-gray-700 mb-4">
            Competitive Differentiators
          </h3>
          <div className="space-y-6">
            {[
              {
                title: "Regulatory & Compliance",
                companies: [
                  data.content.competitive_differentiators
                    .regulatory_and_compliance,
                ],
              },
              {
                title: "Geographic Coverage & Scale",
                companies: [
                  data.content.competitive_differentiators
                    .geographic_coverage_and_scale,
                ],
              },
              {
                title: "Technology Advantage",
                companies: [
                  data.content.competitive_differentiators.technology_advantage,
                ],
              },
            ].map((item, idx) => (
              <div key={idx}>
                <p className="flex text-gray-700 items-center gap-3">
                  {item.title}
                </p>
                <p className="text-sm text-gray-500">
                  {item.companies.join(", ")}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Divider */}
        <div className="hidden lg:block border-l border-dashed border-gray-300 mx-6"></div>

        {/* Right - Industry Environment */}
        <div className="flex-1">
          <h3 className="text-lg text-gray-700 font-medium mb-4">
            Industry Environment
          </h3>
          <div className="grid grid-cols-2 gap-y-3 text-sm text-gray-700">
            <span className="text-gray-500">Characteristic</span>
            <span className="text-gray-500">Trend</span>

            {industryEnvironment.slice(2).map((item, idx) => (
              <React.Fragment key={idx}>
                <span>{item.characteristic}</span>
                <div className="flex items-center gap-4 ">
                  {item.color && (
                    <>
                      <span
                        className={`text-white text-xs px-2 py-0.5 rounded-full ${item.color}`}
                      >
                        {item.trend}
                      </span>
                      {item.reason && (
                        <div className="relative group inline-block">
                          <button
                            onClick={() =>
                              setOpenIndex(openIndex === idx ? null : idx)
                            }
                            className="text-xs text-gray-500 border border-gray-300 px-2 py-0.5 rounded-full hover:bg-gray-100 transition cursor-pointer"
                          >
                            Why?
                          </button>
                          {openIndex === idx && (
                            <div className="absolute left-1/2 bottom-full mb-2  bg-white border border-gray-200 rounded-2xl  text-gray-600 text-xs px-3 py-1  -translate-x-1/2 shadow-md z-10  overflow-y-auto max-h-[8rem] max-w-[12rem] w-max  text-left">
                              {item.reason}
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* Sources */}
      {data.content.sources?.length > 0 && (
        <div className="mt-6 flex flex-wrap gap-2 text-sm text-gray-500 items-center">
          <span>Source(s):</span>
          {data.content.sources.map((src, i) => (
            <button
              key={i}
              onClick={(e) => handleSourceClick(e, src)}
              className="bg-white px-3 py-1 rounded-full border border-gray-200 text-gray-700 cursor-pointer hover:bg-gray-50"
            >
              {new URL(src).hostname}
            </button>
          ))}
        </div>
      )}
      {iframeUrl && (
        <IframeViewer url={iframeUrl} onClose={() => setIframeUrl(null)} />
      )}
    </div>
  );
};

export default Overview;
